import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  return_code: string | null;
  return_reason: string | null;
  initiated_at: string;
  expected_settlement_date: string | null;
  actual_settlement_date: string | null;
  retry_count: number;
  max_retries: number;
  is_retry_eligible: boolean;
  confirmation_code: string;
  fund_type: string;
  institution_name: string;
  account_type: string;
  last4: string;
}

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const fetchTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const url = filter === 'all' 
        ? '/api/v1/hivefund/transactions'
        : `/api/v1/hivefund/transactions?status=${filter}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      setTransactions(data.transactions || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'settled':
      case 'succeeded':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
      case 'failed':
      case 'returned':
        return <XCircleIcon className="w-6 h-6 text-red-500" />;
      case 'processing':
        return <ArrowPathIcon className="w-6 h-6 text-blue-500 animate-spin" />;
      default:
        return <ClockIcon className="w-6 h-6 text-amber-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'settled':
      case 'succeeded':
        return 'text-green-700 bg-green-50';
      case 'failed':
      case 'returned':
        return 'text-red-700 bg-red-50';
      case 'processing':
        return 'text-blue-700 bg-blue-50';
      default:
        return 'text-amber-700 bg-amber-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <ArrowPathIcon className="w-12 h-12 text-amber-600 animate-spin" />
        <div className="ml-4 text-xl text-amber-800">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">Transaction History</h1>
          <p className="text-amber-700">View all your payment transactions</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-2">
          {['all', 'pending', 'processing', 'settled', 'failed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === status
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-white text-amber-900 hover:bg-amber-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No transactions found</h3>
            <p className="text-gray-600">Your payment history will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((tx) => (
              <motion.div
                key={tx.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => setSelectedTransaction(tx)}
                className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-xl transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {getStatusIcon(tx.status)}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-lg font-bold text-gray-900">
                          ${(tx.amount / 100).toLocaleString()}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tx.status)}`}>
                          {tx.status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>
                          <span className="font-medium">Investment:</span> {tx.confirmation_code} ({tx.fund_type})
                        </div>
                        <div>
                          <span className="font-medium">Bank:</span> {tx.institution_name} ••{tx.last4} ({tx.account_type})
                        </div>
                        <div>
                          <span className="font-medium">Initiated:</span> {new Date(tx.initiated_at).toLocaleString()}
                        </div>
                        {tx.expected_settlement_date && (
                          <div>
                            <span className="font-medium">Expected Settlement:</span>{' '}
                            {new Date(tx.expected_settlement_date).toLocaleDateString()}
                          </div>
                        )}
                        {tx.actual_settlement_date && (
                          <div>
                            <span className="font-medium">Settled:</span>{' '}
                            {new Date(tx.actual_settlement_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      {/* Retry Info */}
                      {tx.is_retry_eligible && tx.retry_count < tx.max_retries && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                          <ArrowPathIcon className="w-4 h-4" />
                          <span>Retry {tx.retry_count} of {tx.max_retries} - Next attempt scheduled</span>
                        </div>
                      )}

                      {/* Error Info */}
                      {tx.return_code && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">
                          <XCircleIcon className="w-4 h-4" />
                          <span>{tx.return_code}: {tx.return_reason}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <InformationCircleIcon className="w-6 h-6 text-gray-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Transaction Details Modal */}
        <AnimatePresence>
          {selectedTransaction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTransaction(null)}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Transaction Details</h2>
                
                {/* Status Header */}
                <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-gray-50">
                  {getStatusIcon(selectedTransaction.status)}
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${(selectedTransaction.amount / 100).toLocaleString()}
                    </div>
                    <div className={`font-medium ${getStatusColor(selectedTransaction.status)}`}>
                      {selectedTransaction.status.toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="Transaction ID" value={selectedTransaction.id} />
                  <DetailRow label="Confirmation Code" value={selectedTransaction.confirmation_code} />
                  <DetailRow label="Fund Type" value={selectedTransaction.fund_type} />
                  <DetailRow label="Currency" value={selectedTransaction.currency.toUpperCase()} />
                  <DetailRow label="Bank" value={selectedTransaction.institution_name} />
                  <DetailRow label="Account" value={`••${selectedTransaction.last4} (${selectedTransaction.account_type})`} />
                  <DetailRow label="Initiated" value={new Date(selectedTransaction.initiated_at).toLocaleString()} />
                  {selectedTransaction.expected_settlement_date && (
                    <DetailRow label="Expected Settlement" value={new Date(selectedTransaction.expected_settlement_date).toLocaleDateString()} />
                  )}
                  {selectedTransaction.actual_settlement_date && (
                    <DetailRow label="Actual Settlement" value={new Date(selectedTransaction.actual_settlement_date).toLocaleDateString()} />
                  )}
                  {selectedTransaction.return_code && (
                    <>
                      <DetailRow label="Return Code" value={selectedTransaction.return_code} />
                      <DetailRow label="Return Reason" value={selectedTransaction.return_reason || 'N/A'} />
                    </>
                  )}
                  {selectedTransaction.is_retry_eligible && (
                    <>
                      <DetailRow label="Retry Count" value={`${selectedTransaction.retry_count} of ${selectedTransaction.max_retries}`} />
                      <DetailRow label="Retry Eligible" value="Yes" />
                    </>
                  )}
                </div>

                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="mt-6 w-full bg-amber-600 text-white py-3 rounded-lg font-medium hover:bg-amber-700 transition-colors"
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-gray-500 text-xs mb-1">{label}</div>
    <div className="text-gray-900 font-medium break-words">{value}</div>
  </div>
);

export default TransactionHistory;
